import React, { useMemo, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import './AccountInfo.css';
import { updateProfile, uploadAvatar, changePassword } from '../store/slices/authSlice';
import { pushToast } from '../store/slices/toastSlice';

const initialEditable = (user) => ({
  companyName: user?.companyName || '',
  phoneNumber: user?.phoneNumber || '',
  taxEIN: user?.taxEIN || '',
  address: {
    line1: user?.address?.line1 || '',
    line2: user?.address?.line2 || '',
    city: user?.address?.city || '',
    state: user?.address?.state || '',
    postalCode: user?.address?.postalCode || '',
    country: user?.address?.country || 'US'
  },
  settings: {
    twoFactorEnabled: user?.settings?.twoFactorEnabled || false,
    notifications: {
      email: user?.settings?.notifications?.email ?? true,
      sms: user?.settings?.notifications?.sms ?? false,
    }
  }
});

const AccountInfo = () => {
  const dispatch = useDispatch();
  const { user, updatingProfile, uploadingAvatar, changingPassword } = useSelector(s => s.auth);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(() => initialEditable(user));
  const [pwModal, setPwModal] = useState(false);
  const [pwFields, setPwFields] = useState({ oldPassword: '', newPassword: '', confirm: '' });

  useEffect(() => {
    setForm(initialEditable(user));
  }, [user]);

  const onChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('address.')) {
      const key = name.split('.')[1];
      setForm(f => ({ ...f, address: { ...f.address, [key]: value } }));
    } else if (name.startsWith('settings.notifications.')) {
      const key = name.split('.')[2];
      setForm(f => ({ ...f, settings: { ...f.settings, notifications: { ...f.settings.notifications, [key]: e.target.type === 'checkbox' ? e.target.checked : value } } }));
    } else if (name === 'settings.twoFactorEnabled') {
      setForm(f => ({ ...f, settings: { ...f.settings, twoFactorEnabled: e.target.checked } }));
    } else {
      setForm(f => ({ ...f, [name]: value }));
    }
  };

  const onSave = async () => {
    try {
      const payload = { ...form };
      dispatch(updateProfile(payload)).unwrap()
        .then(() => {
          dispatch(pushToast({ type: 'success', message: 'Profile updated' }));
          setEditing(false);
        })
        .catch(err => {
          dispatch(pushToast({ type: 'error', message: err || 'Update failed' }));
        });
    } catch (e) {
      dispatch(pushToast({ type: 'error', message: 'Unexpected error updating profile' }));
    }
  };

  const onAvatarSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/png','image/jpeg','image/webp'].includes(file.type)) {
      dispatch(pushToast({ type: 'error', message: 'Unsupported image type' }));
      return;
    }
    if (file.size > 1024*1024) {
      dispatch(pushToast({ type: 'error', message: 'Image larger than 1MB' }));
      return;
    }
    dispatch(uploadAvatar(file))
      .unwrap()
      .then(() => dispatch(pushToast({ type: 'success', message: 'Avatar updated' })))
      .catch(err => dispatch(pushToast({ type: 'error', message: err || 'Avatar upload failed' })));
  };

  const onChangePassword = () => {
    if (!pwFields.oldPassword || !pwFields.newPassword) {
      dispatch(pushToast({ type: 'error', message: 'Fill all password fields' }));
      return;
    }
    if (pwFields.newPassword !== pwFields.confirm) {
      dispatch(pushToast({ type: 'error', message: 'Passwords do not match' }));
      return;
    }
    if (pwFields.newPassword.length < 8 || !/[A-Za-z]/.test(pwFields.newPassword) || !/\d/.test(pwFields.newPassword)) {
      dispatch(pushToast({ type: 'error', message: 'Weak password (min 8, letters & numbers)' }));
      return;
    }
    dispatch(changePassword({ oldPassword: pwFields.oldPassword, newPassword: pwFields.newPassword }))
      .unwrap()
      .then(() => {
        dispatch(pushToast({ type: 'success', message: 'Password changed' }));
        setPwFields({ oldPassword: '', newPassword: '', confirm: '' });
        setPwModal(false);
      })
      .catch(err => dispatch(pushToast({ type: 'error', message: err || 'Change failed' })));
  };

  const verificationBadge = useMemo(() => {
    const status = user?.verification?.status;
    if (!status) return null;
    const map = {
      APPROVED: { label: 'Approved', class: 'badge-success' },
      PENDING: { label: 'Pending Review', class: 'badge-warning' },
      REJECTED: { label: 'Rejected', class: 'badge-danger' },
      UNVERIFIED: { label: 'Unverified', class: 'badge-neutral' }
    };
    const meta = map[status] || { label: status, class: 'badge-neutral' };
    return <span className={`badge ${meta.class}`}>{meta.label}</span>;
  }, [user]);

  const twoFactor = user?.settings?.twoFactorEnabled ? 'Enabled' : 'Disabled';
  // notification preferences displayed inline per channel; removed aggregated unused variable

  return (
    <div className="account-info">
      <div className="ai-header">
        <div>
          <h1>Account Information</h1>
          <p className="ai-subtitle">View your profile, business status, and security settings.</p>
        </div>
        <div className="ai-actions">
          {!editing && <button className="ai-btn active" onClick={() => setEditing(true)}>Edit Profile</button>}
          {editing && (
            <>
              <button className="ai-btn" onClick={() => { setForm(initialEditable(user)); setEditing(false); }}>Cancel</button>
              <button className="ai-btn primary" disabled={updatingProfile} onClick={onSave}>{updatingProfile ? 'Saving...' : 'Save'}</button>
            </>
          )}
          <button className="ai-btn" onClick={() => setPwModal(true)}>Change Password</button>
        </div>
      </div>

      {!user && <p>Loading user profile...</p>}
      {user && (
        <div className="ai-grid">
          <section className="ai-card ai-avatar-card">
            <h3>Avatar / Logo</h3>
            <div className="ai-avatar-wrapper">
              <div className={`ai-avatar ${uploadingAvatar ? 'loading' : ''}`}> 
                {user.avatarUrl ? (
                  <img src={user.avatarUrl} alt="Avatar" />
                ) : (
                  <span>{user.companyName?.slice(0,2).toUpperCase()}</span>
                )}
                <label className="ai-avatar-overlay">
                  <input type="file" accept="image/png,image/jpeg,image/webp" onChange={onAvatarSelect} hidden />
                  {uploadingAvatar ? 'Uploading...' : 'Change'}
                </label>
              </div>
            </div>
          </section>
          <section className="ai-card">
            <h3>Business Details</h3>
            <div className="ai-pair"><span>Company Name</span>{editing ? <input name="companyName" value={form.companyName} onChange={onChange} /> : <strong>{user.companyName}</strong>}</div>
            <div className="ai-pair"><span>Account Type</span><strong>{user.accountType}</strong></div>
            <div className="ai-pair"><span>Status</span><strong>{user.isActive ? 'Active' : 'Inactive'}</strong></div>
            <div className="ai-pair"><span>Verification</span><strong>{verificationBadge}</strong></div>
            <div className="ai-pair"><span>Tax EIN</span>{editing ? <input name="taxEIN" value={form.taxEIN} onChange={onChange} /> : <strong>{user.taxEIN || '—'}</strong>}</div>
          </section>

            <section className="ai-card">
              <h3>Contact</h3>
              <div className="ai-pair"><span>Email</span><strong>{user.email}</strong></div>
              <div className="ai-pair"><span>Phone</span>{editing ? <input name="phoneNumber" value={form.phoneNumber} onChange={onChange} /> : <strong>{user.phoneNumber || '—'}</strong>}</div>
              <div className="ai-pair ai-address"><span>Address</span>
                {editing ? (
                  <div className="ai-address-edit">
                    <input name="address.line1" placeholder="Line 1" value={form.address.line1} onChange={onChange} />
                    <input name="address.line2" placeholder="Line 2" value={form.address.line2} onChange={onChange} />
                    <input name="address.city" placeholder="City" value={form.address.city} onChange={onChange} />
                    <input name="address.state" placeholder="State" value={form.address.state} onChange={onChange} />
                    <input name="address.postalCode" placeholder="Postal" value={form.address.postalCode} onChange={onChange} />
                    <input name="address.country" placeholder="Country" value={form.address.country} onChange={onChange} />
                  </div>
                ) : (
                  <div className="ai-address-lines">
                    {user.address?.line1 && <div>{user.address.line1}</div>}
                    {user.address?.line2 && <div>{user.address.line2}</div>}
                    {(user.address?.city || user.address?.state || user.address?.postalCode) && (
                      <div>{[user.address.city, user.address.state, user.address.postalCode].filter(Boolean).join(', ')}</div>
                    )}
                    {user.address?.country && <div>{user.address.country}</div>}
                  </div>
                )}
              </div>
            </section>

            <section className="ai-card">
              <h3>Security & Preferences</h3>
              <div className="ai-pair"><span>Two-Factor Auth</span>{editing ? <input type="checkbox" name="settings.twoFactorEnabled" checked={form.settings.twoFactorEnabled} onChange={onChange} /> : <strong>{twoFactor}</strong>}</div>
              <div className="ai-pair"><span>Email Notifications</span>{editing ? <input type="checkbox" name="settings.notifications.email" checked={form.settings.notifications.email} onChange={onChange} /> : <strong>{user.settings?.notifications?.email ? 'Yes' : 'No'}</strong>}</div>
              <div className="ai-pair"><span>SMS Notifications</span>{editing ? <input type="checkbox" name="settings.notifications.sms" checked={form.settings.notifications.sms} onChange={onChange} /> : <strong>{user.settings?.notifications?.sms ? 'Yes' : 'No'}</strong>}</div>
              {!editing && <div className="ai-note">Change your password or update profile details using the controls above.</div>}
            </section>

            <section className="ai-card">
              <h3>Meta</h3>
              <div className="ai-pair"><span>User ID</span><code className="ai-code">{user._id}</code></div>
              <div className="ai-pair"><span>Created</span><strong>{new Date(user.createdAt || user.created_at || Date.now()).toLocaleString()}</strong></div>
              <div className="ai-pair"><span>Last Updated</span><strong>{new Date(user.updatedAt || user.updated_at || Date.now()).toLocaleString()}</strong></div>
            </section>
        </div>
      )}
      {pwModal && (
        <div className="ai-password-modal">
          <div className="ai-password-dialog">
            <h3>Change Password</h3>
            <label>Old Password<input type="password" value={pwFields.oldPassword} onChange={e => setPwFields(f => ({ ...f, oldPassword: e.target.value }))} /></label>
            <label>New Password<input type="password" value={pwFields.newPassword} onChange={e => setPwFields(f => ({ ...f, newPassword: e.target.value }))} /></label>
            <label>Confirm New<input type="password" value={pwFields.confirm} onChange={e => setPwFields(f => ({ ...f, confirm: e.target.value }))} /></label>
            <div className="ai-password-actions">
              <button onClick={() => setPwModal(false)}>Cancel</button>
              <button disabled={changingPassword} onClick={onChangePassword}>{changingPassword ? 'Changing...' : 'Change Password'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountInfo;
